import { SignupServiceError } from '../../services/SignupService';

export interface SignupActionError {
  reason: 'consent' | 'email' | 'selection' | 'subscription' | 'request' | 'sign-in';
  message: string;
}

export const consentErrorMessage = 'Accept the privacy policy and terms and conditions to continue.';
export const emailErrorMessage = 'Enter a valid school or district email address.';

export type CleverSignupErrorCode =
  | 'clever_cancelled'
  | 'clever_authentication_failed'
  | 'clever_student_not_allowed'
  | 'clever_campus_mismatch'
  | 'clever_continuation_invalid'
  | 'clever_continuation_expired'
  | 'clever_subscription_unavailable';

export type ClassLinkSignupErrorCode =
  | 'classlink_cancelled'
  | 'classlink_authentication_failed'
  | 'classlink_student_not_allowed'
  | 'classlink_campus_mismatch'
  | 'classlink_continuation_invalid'
  | 'classlink_continuation_expired'
  | 'classlink_mapping_unavailable'
  | 'classlink_subscription_unavailable';

export type SchoologySignupErrorCode =
  | 'schoology_authentication_failed'
  | 'schoology_student_not_allowed'
  | 'schoology_continuation_invalid'
  | 'schoology_continuation_expired';

export type CanvasSignupErrorCode =
  | 'canvas_authentication_failed'
  | 'canvas_student_not_allowed'
  | 'canvas_continuation_invalid'
  | 'canvas_continuation_expired';

export function getCanvasSignupErrorMessage(code: string | null): string {
  switch (code) {
    case 'canvas_student_not_allowed':
      return 'Student Canvas accounts cannot be used for educator signup.';
    case 'canvas_continuation_invalid':
    case 'canvas_continuation_expired':
      return 'Your Canvas signup session has expired. Relaunch Exploros from Canvas.';
    case 'canvas_authentication_failed':
      return 'We couldn’t complete Canvas authentication. Relaunch Exploros from Canvas.';
    default:
      return '';
  }
}

export function getSchoologySignupErrorMessage(code: string | null): string {
  switch (code) {
    case 'schoology_student_not_allowed':
      return 'Student Schoology accounts cannot be used for educator signup.';
    case 'schoology_continuation_invalid':
    case 'schoology_continuation_expired':
      return 'Your Schoology signup session has expired. Relaunch Exploros from Schoology.';
    case 'schoology_authentication_failed':
      return 'We couldn’t complete Schoology authentication. Relaunch Exploros from Schoology.';
    default:
      return '';
  }
}

export function getClassLinkSignupErrorMessage(code: string | null): string {
  switch (code) {
    case 'classlink_cancelled':
      return 'ClassLink signup was cancelled. Try again when you’re ready.';
    case 'classlink_student_not_allowed':
      return 'Student ClassLink accounts cannot be used for educator signup.';
    case 'classlink_campus_mismatch':
      return 'The selected campus does not match your ClassLink organization. Choose a mapped campus and try again.';
    case 'classlink_continuation_invalid':
    case 'classlink_continuation_expired':
      return 'Your ClassLink signup session has expired. Start ClassLink signup again.';
    case 'classlink_mapping_unavailable':
      return 'We couldn’t verify your ClassLink organization. Try again later.';
    case 'classlink_subscription_unavailable':
      return 'We couldn’t verify the campus subscription. Try again later.';
    case 'classlink_authentication_failed':
      return 'We couldn’t complete ClassLink authentication. Try again.';
    default:
      return '';
  }
}

export function getCleverSignupErrorMessage(code: string | null): string {
  switch (code) {
    case 'clever_cancelled':
      return 'Clever signup was cancelled. Try again when you’re ready.';
    case 'clever_student_not_allowed':
      return 'Student Clever accounts cannot be used for educator signup.';
    case 'clever_campus_mismatch':
      return 'The selected campus does not match your Clever organization. Choose a mapped campus and try again.';
    case 'clever_continuation_invalid':
    case 'clever_continuation_expired':
      return 'Your Clever signup session has expired. Start Clever signup again.';
    case 'clever_subscription_unavailable':
      return 'We couldn’t verify the campus subscription. Try again later.';
    case 'clever_authentication_failed':
      return 'We couldn’t complete Clever authentication. Try again.';
    default:
      return '';
  }
}

export function getSignupErrorMessage(error: unknown): string {
  if (error instanceof SignupServiceError) {
    if (error.code === 'signup_token_invalid') {
      return 'We couldn’t find an active subscription for that token.';
    }
    if (error.code === 'signup_school_not_listed_unavailable') {
      return 'The school-not-listed option is temporarily unavailable. Please try again later.';
    }
    if (error.code === 'signup_school_state_unavailable') {
      return 'That school is missing state information. Please choose another school or contact support.';
    }
    const cleverMessage = error.code === 'clever_continuation_unavailable'
      ? getCleverSignupErrorMessage('clever_authentication_failed')
      : getCleverSignupErrorMessage(error.code);
    if (cleverMessage) {
      return cleverMessage;
    }
    const classLinkMessage = error.code === 'classlink_continuation_unavailable'
      ? getClassLinkSignupErrorMessage('classlink_authentication_failed')
      : getClassLinkSignupErrorMessage(error.code);
    if (classLinkMessage) {
      return classLinkMessage;
    }
    const schoologyMessage = error.code === 'schoology_continuation_unavailable'
      ? getSchoologySignupErrorMessage('schoology_authentication_failed')
      : getSchoologySignupErrorMessage(error.code);
    if (schoologyMessage) {
      return schoologyMessage;
    }
    const canvasMessage = error.code === 'canvas_continuation_unavailable'
      ? getCanvasSignupErrorMessage('canvas_authentication_failed')
      : getCanvasSignupErrorMessage(error.code);
    if (canvasMessage) {
      return canvasMessage;
    }
  }

  return 'We couldn’t start your signup. Check your connection and try again.';
}
